"use client";

// DocsOsShell — the public docs in the Internet OS navigator grammar, collapsed
// to the docs' own two-level shape:
//   • SPACES  (the ticker siblings) = the doc GROUPS — the top-level headings:
//     start here · for developers · concepts · connect an AI · build on the API ·
//     reference.
//   • VIEWS   (the chips)           = the PAGES inside the active group — the
//     subheadings. Selecting a chip opens that page; its full, extensive prose is
//     the content.
// So the navigator reads exactly like the documentation's own outline, and there
// is one clean control per level instead of a read/contents/all-docs bar on every
// page.
//
// The DOCUMENT stays server-rendered semantic HTML. This shell lives in the docs
// layout, so it persists across route changes; each /docs/* route server-renders
// its prose (DocPageView → DocMarkdown → real h1/h2/p/table/code) and hands it in
// as `children`. Switching a space or a view is a Next client transition — the
// shell and organism never remount, and every URL's HTML still carries the full
// document for any AI or crawler to scan, like a normal docs viewer.
//
// law_stage — the outline is the stage's navigator and the prose scrolls in the
// stage's own box under it. The crumb bar (wordmark, ≡, search, you) stays fixed
// outside the stage, exactly where it was; the ground and netti are painted
// before it. Each page is a place with a real address: a press is a route
// change, and the stage hands the old page over to the new one in the direction
// of travel.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import BrandCrumb, { type Crumb } from "../BrandCrumb";
import SiteMenu, { MENU_ORG, MENU_ORG_SCALE } from "../SiteMenu";
import EnterPill from "../EnterPill";
import UserChip from "../UserChip";
import OrganismBackdrop from "../OrganismBackdrop";
import OsNavigator, { type ChipDef } from "../os/OsNavigator";
import StatusCorner from "../os/StatusCorner";
import DocsInlineSearch from "./DocsInlineSearch";
import { DOC_PAGES, docGroups, docHref, slugifyHeading, type DocPageDef } from "../../lib/docs/content";
import type { OsNode } from "../../lib/os/tree";
import { organismFromSeed, seedFromString } from "../../lib/organism/identity";
import { ageFromMemory } from "../../lib/organism/macro-tuners";
import { applyGroundVars, applyOrganismVars, DOCS_DARK_BG, type RGB } from "../../lib/theme";
import { applyGroundGradient, rgbToHsv01 } from "../../lib/ground";
import GroundDial from "../os/GroundDial";
import Stage from "../stage/Stage";
import { useStageReadRamp } from "../stage/useStage";
import { onStageScroll, stageScrollTop } from "../../lib/stage/scroller";
import { readScroll, saveScroll } from "../../lib/stage/memory";
import { keepResumable } from "../../lib/os/resume";
import { PLACES } from "../../lib/os/places";
import { DOCS_CONTINUE_EVENT } from "./DocsContinue";
import "../stage/surfaces/docs.css";

const useIso = typeof window !== "undefined" ? useLayoutEffect : useEffect;

// this tab's docs ground — sessionStorage, never an account. Absent (or
// unreadable, or garbage) means the midnight default.
const DOCS_GROUND_KEY = "innernet:docs-ground";
// where each docs page was left (lib/stage/memory keeps it by this key + the address)
const DOCS_SCROLL = "docs:";
function readDocsGround(): { rgb: RGB; grad: number } | null {
  try {
    const raw = sessionStorage.getItem(DOCS_GROUND_KEY);
    if (!raw) return null;
    const j = JSON.parse(raw) as { rgb?: unknown; grad?: unknown };
    const rgb = j.rgb;
    if (!Array.isArray(rgb) || rgb.length !== 3 || !rgb.every((n) => typeof n === "number" && n >= 0 && n <= 1)) return null;
    const grad = typeof j.grad === "number" && Number.isFinite(j.grad) ? Math.min(1, Math.max(0, j.grad)) : 0;
    return { rgb: rgb as unknown as RGB, grad };
  } catch {
    return null;
  }
}
function saveDocsGround(rgb: RGB, grad: number): void {
  try {
    sessionStorage.setItem(DOCS_GROUND_KEY, JSON.stringify({ rgb, grad: Number(grad.toFixed(3)) }));
  } catch {
    /* private mode — the pick still stands for this page */
  }
}

// every custom property the docs write onto :root while they are open
// (applyOrganismVars · applyGroundVars · applyGroundGradient) — restored on unmount.
const BORROWED_VARS = [
  "--bg", "--ink-sat", "--ink-light",
  "--g-a", "--g-b", "--g-c", "--g-top",
  "--organism-hue", "--organism-color", "--organism-core", "--organism-fringe", "--on-organism",
];

// the docs get the brand's own organism — deterministic, so the bloom + every
// tinted surface (chip-active ink, dots, glows) are the same on every visit.
const BRAND_SEED = "innernet";

const pageId = (p: DocPageDef) => p.slug || "overview";
const pageLabel = (p: DocPageDef) => (p.title === "innernet docs" ? "overview" : p.title);

// a one-line Noticer hint per space, so hovering a group explains what it holds.
const GROUP_HINT: Record<string, string> = {
  "start here": "start here — the fastest way in: sign up, connect, save your first context.",
  "your innernet": "your innernet — the app, place by place: the dashboard, projects, you, the inbox, sharing, the chat window.",
  "for developers": "for developers — memory that versions with your code; connect once and it follows the repo.",
  "concepts": "concepts — how the memory behaves: the map, retrieval, branches, personal memory, forgetting.",
  "connect an ai": "connect an AI — wire any MCP client to one URL, per-tool.",
  "build on the api": "build on the API — the REST endpoints and the typed SDK.",
  "agents": "agents — memory for the agents you keep: each has its own, and what it learns about you comes to you.",
  "reference": "reference — every MCP tool, and how your data is handled.",
};

export default function DocsOsShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();

  // ── the docs' outline: groups are spaces, their pages are the views ──────────
  const groups = useMemo(() => docGroups(), []);
  const routeSlug = pathname === "/docs" ? "" : pathname.replace(/^\/docs\/?/, "");
  // THE NAVIGATOR ANSWERS AT ONCE (law_snappy, 2026-09-29). The ticker and the
  // chips used to read the active page off the URL, and the URL only changes
  // once the next page has arrived: press a word and nothing moved until the
  // network answered, then the whole rail jumped. The press now names where we
  // are going (`pending`) and the rail glides there on the same frame; the
  // route catches up underneath and clears it.
  // The route arriving clears it, in the render that sees the new path (React's
  // "adjust state while rendering"), so Back to where the press began can never
  // revive a stale destination.
  const [pending, setPending] = useState<{ slug: string; from: string } | null>(null);
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    setPending(null);
  }
  const activeSlug = pending && pending.from === pathname ? pending.slug : routeSlug;
  const go = useCallback(
    (p: DocPageDef) => {
      if (p.slug === routeSlug) return;
      setPending({ slug: p.slug, from: pathname });
      router.push(docHref(p));
    },
    [router, routeSlug, pathname],
  );
  // every docs page is a static document, so fetch them all while the reader
  // is reading: a press then only has to swap what is already here.
  useEffect(() => {
    const warm = () => { for (const p of DOC_PAGES) router.prefetch(docHref(p)); };
    const ric = (window as unknown as { requestIdleCallback?: (cb: () => void) => number }).requestIdleCallback;
    if (ric) ric(warm);
    else window.setTimeout(warm, 400);
  }, [router]);
  const activePage = useMemo(
    () => DOC_PAGES.find((p) => p.slug === activeSlug) ?? DOC_PAGES[0],
    [activeSlug],
  );
  const activeGroup = useMemo(
    () => groups.find((g) => g.pages.some((p) => p.slug === activePage.slug)) ?? groups[0],
    [groups, activePage],
  );
  const activeGroupId = slugifyHeading(activeGroup.title);

  // the "docs" space — this page's own sections (the doc groups). Selecting one
  // opens its first page. innernet + (founders) are appended by buildSpaces.
  const docsSpace = useMemo(
    () => ({
      id: "docs",
      label: "docs",
      // THE WHOLE INDEX, IN THE MENU (2026-09-29). Each group is a pure
      // directory holding every one of its pages, so the ≡ reads as the docs'
      // table of contents: tap a group to see what is inside, tap a page to go.
      // (A group with a page of its own would open on the first tap and never
      // show its contents, so it carries no onSelect — see siteNav MenuItem.)
      items: groups.map((g) => ({
        key: slugifyHeading(g.title),
        label: g.title,
        here: slugifyHeading(g.title) === activeGroupId,
        children: g.pages.map((p) => ({
          key: pageId(p),
          label: pageLabel(p),
          here: p.slug === activePage.slug,
          onSelect: () => go(p),
        })),
      })),
    }),
    [groups, activeGroupId, activePage, go],
  );

  // SPACES — one ticker sibling per group. Its stat is how many pages (views) it holds.
  const siblings = useMemo<OsNode[]>(
    () =>
      groups.map((g) => ({
        key: slugifyHeading(g.title),
        path: docHref(g.pages[0]),
        kind: "group",
        name: g.title,
        symbol: "",
        icon: "folder",
        summary: GROUP_HINT[g.title.toLowerCase()] ?? "",
        stats: [{ label: g.pages.length === 1 ? "page" : "pages", value: g.pages.length }],
      }) as OsNode),
    [groups],
  );

  // VIEWS — one chip per page in the active group. A single-page group shows no
  // chip bar (nothing to switch), like a folder with one item.
  const chips = useMemo<ChipDef[]>(
    () => (activeGroup.pages.length > 1 ? activeGroup.pages.map((p) => ({ id: pageId(p), label: pageLabel(p) })) : []),
    [activeGroup],
  );

  // the navigator is the stage's: it folds it (with the scroll-up reveal), and
  // its folding half publishes its natural height itself (OsNavigator `fold`).

  // ── directional slide, keyed off the flat page index (rises across groups too).
  // Decided DURING the render that mounts the new page: it used to be written
  // in a layout effect, which runs after that render had already read it, so
  // every slide came in from the side of the step BEFORE (going right after a
  // left step slid in from the left).
  const routeIdx = DOC_PAGES.findIndex((p) => p.slug === routeSlug);
  // (React's "adjust state while rendering" pattern: the render that sees the
  // new index re-runs at once with the direction set, before anything commits.)
  const [slide, setSlide] = useState({ idx: routeIdx, dir: 1 });
  if (slide.idx !== routeIdx) setSlide({ idx: routeIdx, dir: routeIdx >= slide.idx ? 1 : -1 });
  const [pulse, setPulse] = useState(0);
  useIso(() => { setPulse((p) => p + 1); }, [pathname]);

  // ── WHERE YOU WERE comes back with the page. The stage scrolls an element, so
  // the browser's own scroll restoration knows nothing of it: the offset of
  // every page is kept by its address (lib/stage/memory), and Back / Forward
  // open the page where it was left. A press (a push) opens at the top.
  const [popped, setPopped] = useState<{ path: string; top: number } | null>(null);
  const pathRef = useRef(pathname);
  pathRef.current = pathname;
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      // a step through the hash on this same page is the browser's own
      if (path === pathRef.current) return;
      setPopped({ path, top: readScroll(DOCS_SCROLL + path) ?? 0 });
    };
    window.addEventListener("popstate", onPop);
    // "continue where you were" on the index: the page opens where it was left, as Back would open it
    const onContinue = (e: Event) => {
      const d = (e as CustomEvent<{ path?: unknown; top?: unknown }>).detail;
      if (typeof d?.path === "string" && d.path !== pathRef.current) setPopped({ path: d.path, top: Math.max(0, Number(d.top) || 0) });
    };
    window.addEventListener(DOCS_CONTINUE_EVENT, onContinue);
    return () => {
      window.removeEventListener("popstate", onPop);
      window.removeEventListener(DOCS_CONTINUE_EVENT, onContinue);
    };
  }, []);
  const poppedRef = useRef(popped);
  poppedRef.current = popped;
  useEffect(() => {
    // a page opened at an address that names a heading (a search hit, a link
    // from another page) goes to it: the stage opens every new place at its top,
    // after the router's own scroll to the hash, so it is asked again here (the
    // scroller's scroll-padding puts it under the navigator). Back / Forward
    // keep the offset the page was left at instead.
    let id = window.location.hash.slice(1);
    try { id = decodeURIComponent(id); } catch { /* a malformed hash names nothing */ }
    const back = poppedRef.current?.path === pathname;
    if (id && !back) document.getElementById(id)?.scrollIntoView({ block: "start" });
    // the arrival has used the offset: a later push to the same page opens at its top
    setPopped(null);
  }, [pathname]);
  useEffect(() => {
    // the docs page being read, and how far, for a later visit (lib/os/places docs): the index offers it
    // back as "continue where you were". The index itself is not a place to continue to.
    const title = DOC_PAGES.find((p) => p.slug === routeSlug)?.title;
    const place = (top: number) => {
      if (routeSlug && title) keepResumable(PLACES.docs(), { path: pathname, top: Math.round(top), title });
    };
    place(0);
    let later: ReturnType<typeof setTimeout> | null = null;
    const keep = () => {
      const top = stageScrollTop();
      saveScroll(DOCS_SCROLL + pathname, top);
      if (later) clearTimeout(later);
      later = setTimeout(() => place(top), 160);
    };
    const off = onStageScroll(keep);
    return () => {
      off();
      if (later) clearTimeout(later);
    };
  }, [pathname, routeSlug]);

  // ── responsive + preloader (mirrors OsShell: organism grows in, chrome reveals)
  const [isNarrow, setIsNarrow] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const [revealed, setRevealed] = useState(false);
  // the site menu takeover — netti rises to centre stage (the MENU_ORG
  // choreography), the header lifts above the veil. The you-menu is the second
  // takeover and behaves identically, so both feed ONE flag: netti, the header
  // z-order and the chrome fade never have to know which one is up.
  const [navOpen, setNavOpen] = useState(false);
  const menuOpen = navOpen;
  const firstReveal = useRef(true);
  useEffect(() => {
    const id = setTimeout(() => setRevealed(true), 70);
    const done = setTimeout(() => { firstReveal.current = false; }, 1900);
    return () => { clearTimeout(id); clearTimeout(done); };
  }, []);

  // on a phone the top row is tight (wordmark + crumb + search + dashboard), so the
  // inline search collapses to a magnifying-glass that expands on tap; on desktop
  // it's always the full "/ search docs" field. Same principle as OsShell.
  const searchCollapsible = isNarrow;

  // ── the docs organism — branded, system-wide palette. applyOrganismVars (not
  //    the persisting setOrganism) so visiting docs never clobbers a signed-in
  //    user's saved palette; it just tints this surface for the session.
  const brandOrg = useMemo(() => organismFromSeed(BRAND_SEED), []);
  const brandSeed = useMemo(() => seedFromString(BRAND_SEED), []);
  // ⚠️ THE DOCS BORROW :root AND GIVE IT BACK (2026-09-29). Both writers below
  // paint the document root, and nothing ever un-painted it: walk from the docs
  // to /community by a client link and the pale rooms inherited the docs'
  // midnight ink regime (--ink-light 90%), white words on sand. Snapshot what
  // was there before the docs touched it and restore it on the way out.
  useIso(() => {
    const st = document.documentElement.style;
    const prev = BORROWED_VARS.map((k) => [k, st.getPropertyValue(k)] as const);
    return () => {
      for (const [k, v] of prev) {
        if (v) st.setProperty(k, v);
        else st.removeProperty(k);
      }
    };
  }, []);
  useIso(() => { applyOrganismVars(brandOrg); }, [brandOrg]);
  // ── the docs ground — always a dark grey-blue by default, independent of
  //    any signed-in dashboard ground preference. applyGroundVars (not the
  //    persisting setBackground) so this only tints the session's /docs
  //    surface and never writes over the user's saved dashboard ground.
  //    Unless THIS TAB has already picked one: the docs wear the same ground
  //    dial as the dashboard (the arc picker, bottom-left), signed in or not,
  //    and the pick lives in sessionStorage — this tab, this visit, nobody's
  //    account. A new tab is the midnight docs again.
  const [docsGround, setDocsGround] = useState<RGB>(DOCS_DARK_BG.rgb);
  const [docsGrad, setDocsGrad] = useState(0);
  useIso(() => {
    const saved = readDocsGround();
    const rgb = saved?.rgb ?? DOCS_DARK_BG.rgb;
    applyGroundVars(rgb);
    if (saved) {
      setDocsGround(saved.rgb);
      setDocsGrad(saved.grad);
      const { h, s, v } = rgbToHsv01(saved.rgb);
      applyGroundGradient(h, s, v, saved.grad);
    }
  }, []);
  const commitDocsGround = (rgb: RGB, grad: number) => {
    setDocsGround(rgb);
    setDocsGrad(grad);
    applyGroundVars(rgb);
    const { h, s, v } = rgbToHsv01(rgb);
    applyGroundGradient(h, s, v, grad);
    saveDocsGround(rgb, grad);
  };
  const baseAge = useMemo(() => ageFromMemory(48), []);

  // DOCS READER RAMP (law_read_ramp) — docs is a reading surface: netti is sharp
  // at the top, and as you scroll into the prose the .os-read-scrim frosts it away
  // (visual blur) WHILE the organism rests (dynamic rendering: fps + backing
  // resolution + hot shader loops wind down with the same ramp). One value drives
  // both — the exact behaviour the dashboard has, now on docs. Resets at the top.
  //
  // Written through the ONE ramp writer (law_ramp_channels), heard from the
  // stage's scroller (components/stage/useStage): docs also folds its navigator
  // (law_scroll_lock), a LAYOUT property that has to step on the coarse `--os-fold`
  // channel rather than reflow the whole page ~100 times across the band.
  // A new page restarts it at the top; the organism rests under the "docs" key.
  useStageReadRamp(220, "os", { calmKey: "docs", key: pathname });

  const introNow = revealed && firstReveal.current;
  const mobileScale = isNarrow ? 0.82 : 1;
  const orgScale = (revealed ? 1 : 0.3) * mobileScale;
  const orgTransMs = introNow ? 1500 : 900;
  const orgEase = introNow ? "cubic-bezier(0.16, 1, 0.3, 1)" : "var(--ease-soft)";

  // ── the live breadcrumb: innernet / docs / <group>. It updates as you move
  //    between spaces; the group crumb links to that group's first page.
  const crumbs: Crumb[] = useMemo(() => {
    const base: Crumb[] = [{ label: "docs", href: "/docs" }];
    // on a phone the ticker already shows the active space prominently, so the
    // breadcrumb stays short (innernet / docs) to leave room for the search glyph
    // + dashboard; on desktop it carries the live space (innernet / docs / group).
    if (isNarrow) return base;
    return [...base, { label: activeGroup.title, href: docHref(activeGroup.pages[0]) }];
  }, [activeGroup, isNarrow]);

  return (
    // overflow-x stays CLIP, never hidden: `hidden` makes this a scroll
    // container, and anything inside that sticks (law_scroll_lock) would stick to it.
    // --gd-lift: the docs carry no share/options dock, so the ground dial has no
    // line to centre on — give it the corner's own breathing room (the same
    // seat a shared room gives it).
    <main style={{ position: "relative", minHeight: "100vh", overflowX: "clip", ["--gd-lift" as string]: "0.4rem" } as React.CSSProperties}>
      <OrganismBackdrop
        readingFrost
        anchor="bottom"
        offset={menuOpen ? MENU_ORG.offset : undefined}
        size="min(150vh, 1120px)"
        scale={menuOpen ? (isNarrow ? MENU_ORG_SCALE.narrow : MENU_ORG_SCALE.wide) : orgScale}
        innerTransMs={menuOpen ? MENU_ORG.innerTransMs : orgTransMs}
        innerEase={menuOpen ? MENU_ORG.innerEase : orgEase}
        blur={0}
        opacity={menuOpen ? MENU_ORG.opacity : revealed ? (isNarrow ? 0.42 : 0.52) : 0}
        fade={0.82}
        zIndex={menuOpen ? MENU_ORG.zIndex : 0}
        spin
        pulse={pulse}
        age={baseAge}
        color={brandOrg}
        seed={brandSeed}
        renderScale={1}
        publishHue={false}
        live={false}
        macros={{ bleed: 0.5, shimmer: 0.4, glow: 0, grain: 0, density: 0.52, network: 0.5 }}
        grow
        glide={menuOpen ? MENU_ORG.glide : false}
        glideAxis={menuOpen ? MENU_ORG.glideAxis : undefined}
      />

      {/* law_read_ramp — netti is sharp at the top; as the prose scrolls this
          ground-aware frost fades in over it (opacity IS --os-ramp) so reading
          lands in full contrast, and clears again at the top. */}
      <div className="os-read-scrim" aria-hidden />

      {/* NO corner veils here: the stage's seam (a thin frost where the prose lets
          go under the navigator) already seats the header AND the navigator. Two
          corner blurs on top of it would double up — exactly the "blur rectangle
          over the chrome" the dashboard learned to drop. */}

      {/* header — the exact dashboard lockup: wordmark + live breadcrumb + inline
          search on the left (baseline-aligned, --t-crumb, wordmark size="inherit"
          so "innernet" and the crumbs match), one quiet link on the right. */}
      <header
        style={{
          position: "fixed", top: 0, left: 0, right: 0,
          padding: "1.4rem 0", zIndex: menuOpen ? 60 : 5,
          opacity: revealed ? 1 : 0, transition: "opacity 0.7s ease",
          // law_mapped_controls: only the visible row takes the pointer
          pointerEvents: "none",
        }}
      >
        <div style={{ pointerEvents: "auto", maxWidth: "var(--os-shell-max)", margin: "0 auto", paddingInline: "var(--os-gutter)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", minWidth: 0, fontSize: "var(--t-crumb)" }}>
            {/* law_no_clipping — no overflow:hidden here: it cropped the wordmark
                leaf-flick + i-dot and the ≡ on tight mobile crumbs. minWidth:0 +
                the crumb's own truncation keep long trails in check. */}
            <span style={{ display: "inline-flex", minWidth: 0, overflow: "visible", flex: "0 1 auto" }}>
              <BrandCrumb segments={crumbs} size="inherit" rootHref="/" afterRoot={<SiteMenu mode="button" current={docsSpace} hereKey="docs" startKey="docs" onOpenChange={setNavOpen} />} style={{ flex: "0 1 auto", minWidth: 0, overflow: "visible" }} />
            </span>
            {/* the search fades away with the rest of the chrome while the menu
                takes over — only the crumb + ≡ stay above the veil. */}
            <span style={{ display: "inline-flex", minWidth: 0, opacity: menuOpen ? 0 : 1, pointerEvents: menuOpen ? "none" : undefined, transition: "opacity 0.4s var(--ease-soft)" }}>
              <DocsInlineSearch collapsible={searchCollapsible} />
            </span>
          </div>
          {/* top-right: the door in for a stranger, YOU once you're signed in.
              UserChip resolves that itself, so /docs stays statically rendered. */}
          <div
            style={{
              display: "flex", alignItems: "center", gap: "1rem", flexShrink: 0,
              opacity: menuOpen ? 0 : 1,
              pointerEvents: menuOpen ? "none" : undefined,
              transition: "opacity 0.4s var(--ease-soft)",
            }}
          >
            <UserChip fallback={<EnterPill onClick={() => router.push("/dashboard")} />} />
          </div>
        </div>
      </header>

      {/* law_stage — the content column is the stage: the outline is its navigator
          (the group ticker, the page description, the page chips), over the top of
          the prose's own scroll area, in the dashboard's own column (the shell's cap
          + gutter + top offset). The stage folds it to its title strip and the
          chips as you read, opens it again on a scroll up, and hands each page
          over to the next in the direction of travel. It fades in with the rest
          of the chrome. */}
      <Stage
        frame="shell"
        autoFold
        headClassName="os-lock--detached"
        sceneKey={pathname}
        dir={slide.dir >= 0 ? 1 : -1}
        swipe
        restoreTop={popped && popped.path === pathname ? popped.top : null}
        slack="7rem"
        label="docs"
        rootProps={{ className: "stage--docs", style: { opacity: revealed ? 1 : 0, transition: "opacity 0.8s var(--ease-soft)" } }}
        head={
          <OsNavigator
            siblings={siblings}
            activeKey={activeGroupId}
            onSelectSibling={(key) => {
              const g = groups.find((x) => slugifyHeading(x.title) === key);
              if (g) go(g.pages[0]);
            }}
            summary={activePage.description}
            summaryManual={false}
            onEditSummary={() => { /* the summary is the active page's description — read only */ }}
            chips={chips}
            view={pageId(activePage)}
            onView={(id) => {
              const p = activeGroup.pages.find((x) => pageId(x) === id);
              if (p) go(p);
            }}
            onNewView={() => { /* fixed view-set */ }}
            onReorderChips={() => { /* pages have a fixed order */ }}
            allowNewView={false}
            fold
          />
        }
      >
        {/* the server-rendered prose for this page; the stage keys it by the path,
            so a space/view change plays the directional slide */}
        {children}
      </Stage>

      {/* the ground-colour dial — the same corner orb + bloom-out arcs the
          dashboard has, pointed at THIS tab's docs ground (controlled mode, so
          nothing is written to an account, signed in or not). */}
      {!menuOpen && <GroundDial value={docsGround} grad={docsGrad} onCommit={commitDocsGround} />}

      <StatusCorner />

      <style>{docsStyles}</style>
    </main>
  );
}

// ── styles ────────────────────────────────────────────────────────────────
// The prose styles (they dress the server-rendered semantic HTML) plus the small
// chrome the OS shell adds. The search styling comes from the shared OS classes
// (.os-crumb-search / .os-search-results) in globals.css.
const docsStyles = `
  .docs-headlink { color: var(--ink-3); transition: color var(--dur-fast) var(--ease-soft); }
  .docs-headlink:hover { color: var(--accent-ink); }
  .docs-headlink-strong { color: var(--accent-ink); }

  /* ── prose (dresses the server-rendered semantic HTML) ── */
  .docs-sr-title { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
  .docs-prose { max-width: 46rem; color: var(--ink-2); }
  .docs-prose > :first-child { margin-top: 0; }
  .docs-prose h2.doc-h {
    font-size: var(--t-h2); line-height: 1.12; letter-spacing: -0.018em; font-weight: 400;
    color: var(--ink); margin-top: var(--space-7); padding-top: var(--space-5);
    border-top: 1px solid var(--hair);
  }
  .docs-prose h2.doc-h:first-child { margin-top: 0; padding-top: 0; border-top: none; }
  .docs-prose h3.doc-h { font-size: var(--t-h3); line-height: 1.2; font-weight: 400; color: var(--ink); margin-top: var(--space-6); }
  .doc-anchor { margin-left: 0.45rem; color: var(--ink-4); opacity: 0; transition: opacity var(--dur-fast) var(--ease-soft); text-decoration: none; }
  .doc-h:hover .doc-anchor { opacity: 1; }
  .docs-prose p { margin-top: var(--space-4); line-height: 1.7; }
  .docs-prose strong { color: var(--ink); font-weight: 500; }
  .docs-prose em { color: var(--ink); }
  .docs-prose a {
    color: var(--accent-ink); text-decoration: underline;
    text-decoration-color: color-mix(in oklab, var(--accent-ink) 35%, transparent);
    text-underline-offset: 3px; transition: text-decoration-color var(--dur-fast) var(--ease-soft);
  }
  .docs-prose a:hover { text-decoration-color: var(--accent-ink); }
  .docs-prose ul, .docs-prose ol { margin-top: var(--space-4); padding-left: 1.2rem; display: flex; flex-direction: column; gap: 0.45rem; }
  .docs-prose ul { list-style: none; }
  .docs-prose ol { list-style: decimal; color: var(--ink-3); }
  .docs-prose ol li { padding-left: 0.25rem; color: var(--ink-2); }
  .docs-prose li { position: relative; line-height: 1.62; }
  .docs-prose ul > li::before { content: ""; position: absolute; left: -1rem; top: 0.66em; width: 4px; height: 4px; border-radius: 50%; background: var(--ink-4); }
  .docs-prose code { font-family: var(--font-mono); font-size: 0.86em; background: var(--surface-2); border: 1px solid var(--hair); padding: 0.08rem 0.4rem; border-radius: var(--radius-sm); color: var(--accent-ink); }
  .docs-prose hr { border: none; border-top: 1px solid var(--hair); margin: var(--space-6) 0; }

  /* a code block: a faint fill with no line round it or under its caption (2026-10-25), THE copy disc in the caption */
  .doc-code { margin-top: var(--space-4); border-radius: var(--radius-field, 12px); overflow: hidden; background: color-mix(in oklab, var(--ink) 5%, transparent); }
  .doc-code figcaption { display: flex; justify-content: space-between; align-items: center; padding: 0.45rem 0.5rem 0 0.9rem; font-family: var(--font-mono); font-size: 0.68rem; color: var(--ink-4); }
  .doc-code pre { padding: 0.9rem 1rem; overflow-x: auto; }
  .doc-code pre code { background: transparent; border: none; padding: 0; color: var(--ink-2); font-size: 0.8rem; line-height: 1.7; white-space: pre; }

  .doc-callout { margin-top: var(--space-5); padding: 1rem 1.25rem; border-left: 2px solid var(--accent); background: color-mix(in oklab, var(--surface) 72%, transparent); border-radius: var(--radius-sm); color: var(--ink-2); line-height: 1.65; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
  .doc-callout p { margin-top: 0.4rem; }
  .doc-callout p:first-child { margin-top: 0; }

  .doc-table-wrap { margin-top: var(--space-4); overflow-x: auto; border: 1px solid var(--hair); border-radius: var(--radius); }
  .docs-prose table { width: 100%; border-collapse: collapse; font-size: var(--t-sm); }
  .docs-prose th { text-align: left; font-weight: 400; color: var(--ink-4); font-size: var(--t-label); letter-spacing: 0.04em; padding: 0.55rem 0.9rem; border-bottom: 1px solid var(--hair); background: color-mix(in oklab, var(--surface) 70%, transparent); }
  .docs-prose td { padding: 0.55rem 0.9rem; border-bottom: 1px solid var(--hair); color: var(--ink-2); line-height: 1.55; vertical-align: top; }
  .docs-prose tr:last-child td { border-bottom: none; }

  .doc-clients { margin-top: var(--space-6); display: grid; gap: 0.7rem; grid-template-columns: repeat(auto-fill, minmax(158px, 1fr)); }
  .doc-client-card { display: flex; flex-direction: column; gap: 0.3rem; padding: 0.95rem 1rem; background: color-mix(in oklab, var(--surface) 72%, transparent); border: 1px solid var(--hair); border-radius: var(--radius); text-decoration: none !important; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); transition: border-color var(--dur-fast) var(--ease-soft), transform var(--dur-fast) var(--ease-soft); }
  .doc-client-card:hover { border-color: var(--line); transform: translateY(-1px); }
  .doc-client-name { color: var(--ink); font-size: 0.92rem; margin-top: 0.25rem; }
  .doc-client-note { color: var(--ink-4); font-size: var(--t-label); letter-spacing: 0.04em; }

  .doc-endpoints { margin-top: var(--space-5); display: flex; flex-direction: column; gap: 0.6rem; }
  .doc-endpoint { border: 1px solid var(--hair); border-radius: var(--radius); background: color-mix(in oklab, var(--surface) 72%, transparent); overflow: hidden; backdrop-filter: blur(6px); -webkit-backdrop-filter: blur(6px); }
  .doc-endpoint + .doc-endpoint { margin-top: 0.6rem; }
  .doc-endpoint summary { display: flex; align-items: center; gap: 0.7rem; flex-wrap: wrap; padding: 0.7rem 1rem; cursor: pointer; list-style: none; }
  .doc-endpoint summary::-webkit-details-marker { display: none; }
  .doc-method { font-family: var(--font-mono); font-size: 0.64rem; letter-spacing: 0.1em; padding: 0.14rem 0.5rem; border-radius: var(--radius-pill); border: 1px solid var(--hair); color: var(--ink-3); background: var(--bg); min-width: 3.4rem; text-align: center; }
  .doc-method-get { color: var(--accent-ink); border-color: color-mix(in oklab, var(--accent) 45%, transparent); }
  .doc-method-post { color: var(--cite-self); }
  .doc-method-delete { color: var(--cite-project); }
  .doc-endpoint-url { background: transparent !important; border: none !important; padding: 0 !important; color: var(--ink) !important; font-size: 0.82rem !important; }
  .doc-endpoint-summary { color: var(--ink-4); font-size: var(--t-sm); }
  .doc-endpoint-body { padding: 0.2rem 1rem 1rem; border-top: 1px solid var(--hair); }
  .doc-endpoint-body p { margin-top: 0.7rem; font-size: var(--t-sm); }
  .doc-endpoint-label { margin: 0.9rem 0 0.3rem; color: var(--ink-4); }
  .doc-endpoint-body table { font-size: var(--t-sm); }
  .doc-endpoint-body td { padding: 0.35rem 0.6rem 0.35rem 0; border-bottom: 1px solid var(--hair); }
  .doc-endpoint-dim { color: var(--ink-4); white-space: nowrap; }

  /* the whole index, in the page (:::index) */
  .doc-continue { margin: var(--space-5) 0 0; padding-left: 18px; color: var(--ink-3); font-size: var(--t-sm); text-wrap: pretty; }
  .doc-index { margin-top: var(--space-5); display: grid; gap: var(--space-6) var(--space-7); grid-template-columns: repeat(auto-fill, minmax(min(100%, 19rem), 1fr)); }
  .doc-index__title { margin: 0 0 0.35rem; padding-left: 18px; color: var(--ink-3); font-size: var(--t-sm); text-wrap: balance; }
  .doc-index .list-row { text-decoration: none !important; flex-direction: column; align-items: flex-start; gap: 0.15rem; padding-block: 0.6rem; }
  .doc-index .list-name { color: var(--ink); }
  /* the shared row pushes its meta right on one line; here it is the page's
     promise, so it sits under the name and wraps */
  .doc-index .list-row .doc-index__d { margin-left: 0; white-space: normal; text-align: left; line-height: 1.45; text-wrap: pretty; padding-inline: 6px; max-width: 100%; }
`;
